/**
 * src/components/FolderTree.tsx — the recursive folder tree: expand/collapse, selection, context
 * menu (New Subfolder / Rename / Move to... / Delete), and the folder half of drag & drop.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/FolderTree.tsx` (whole file, 501 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §10.1, §10.3, §15 D-6/D-27, §16 ranks 8/17.
 *
 * Mounted **twice** by the sidebar — once per tab — each with independent expansion and selection
 * state (`EnhancedSavedDocuments.tsx:184-189`, `:295-299`). Both pass `hideCreateButton={true}` and
 * drive creation through the imperative handle instead.
 *
 * ═══ DF-22 DEFECTS REPRODUCED HERE ════════════════════════════════════════════════════════════
 *
 *  - **rank 8 — folder cycles make folders vanish.** `buildFolderTree`'s `if (parent)` has no
 *    `else`: a node whose `parent_id` is not in the map is dropped from the tree silently. Nothing
 *    validates `parent_id` client-side and nothing does server-side either, so the drag handler in
 *    `EnhancedSavedDocuments` can create `A → B → A` and **both folders disappear** with no error
 *    and no way back through the UI. This is the mechanism; the trigger is over there. See
 *    `buildFolderTree` below.
 *
 *  - **rank 17 — folder names are not trimmed.** `handleCreateFolder` trims only for the *validity
 *    check* and then sends the raw `newFolderName` (`:284` vs `:291`), so `'  x  '` is stored with
 *    its spaces. `handleRenameFolder` does the same (`:305` vs `:313`). Names are also not
 *    truncated in this tree (`:143`) although `MoveDialog` truncates (`MoveDialog.tsx:172`) — a
 *    long name pushes the row wide inside the `ScrollArea`.
 *
 *  - **rank 10 — the breadcrumb goes stale after a rename.** Caused by `useUpdateFolder`'s
 *    invalidation set, not by this file, but this file is where renames originate.
 *
 *  - **rank 7 — `Move to... → Root` silently does nothing** while toasting success. Lives in
 *    `MoveDialog`; reached from this file's context menu.
 *
 * ═══ OTHER SHIPPED QUIRKS PRESERVED ═══════════════════════════════════════════════════════════
 *  - Everything starts **collapsed** — `expandedFolders` initialises to an empty Set (`:195`).
 *  - Clicking a row **selects** it; it does not expand it. Expansion is chevron-only (`:119-136`).
 *  - The **Root row has no context menu** (`:353-361`), so a root-level folder cannot be created by
 *    right-click — only via the sidebar's `New Folder` button when nothing is selected.
 *  - Selection is duplicated: this component keeps `selectedFolder` *and* notifies the parent, which
 *    keeps its own copy (spec §10.1).
 *  - `folderTree` is rebuilt on **every render** (`:264`) — not memoised.
 *  - `useImperativeHandle` has **no dependency array** (`:231-235`), so the handle object is new
 *    every render. Harmless here; faithful.
 *
 * ═══ TWO DEAD BINDINGS DROPPED (spec §10.1, §13 — behaviour-neutral) ══════════════════════════
 *  1. `useUpdateDocument` was imported and destructured (`:2`, `:210`) and never referenced. Its
 *     import is gone. A `useMutation` that is never invoked has no observable effect.
 *  2. `onLoadDocument` was destructured (`:193`) and never referenced. The **prop stays declared**
 *     so `EnhancedSavedDocuments.tsx:186` can keep passing it exactly as the source does — only the
 *     unused binding is gone. Spec §10.1 says "Drop both in the port".
 *  Under `noUnusedLocals` / `no-unused-vars` neither could be kept as written. Reported.
 */
import { useState, forwardRef, useImperativeHandle, type CSSProperties } from 'react'
import { useFolders, useCreateFolder, useUpdateFolder, useDeleteFolder } from '@/hooks/use-database'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  Edit2,
  FolderPlus,
  Home,
  Move
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { toast } from 'sonner'
import { MoveDialog } from './MoveDialog'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { Folder as FolderRow } from '@/lib/types'

/** Imperative handle — `FolderTree.tsx:47-51`, exposed at `:231-235`. */
export interface FolderTreeHandle {
  openCreateDialog: (parentId: number | null) => void
  openDeleteDialog: (folderId: number) => void
  openMoveDialog: (folderId: number) => void
}

/** Props — `FolderTree.tsx:53-57`. */
interface FolderTreeProps {
  /**
   * **Dead prop.** Declared and passed by `EnhancedSavedDocuments.tsx:186` (documents tab only, not
   * the preambles tab) but never referenced inside this component — `FolderTree.tsx:54,193`. Kept in
   * the contract so the call site is byte-for-byte the source's; deliberately not destructured.
   */
  onLoadDocument?: (latexContent: string, htmlContent: string) => void
  onFolderSelect?: (folderId: number | null) => void
  hideCreateButton?: boolean
}

/** `FolderTree.tsx:59-64`. */
interface FolderNode {
  id: number
  name: string
  parent_id: number | null
  children: FolderNode[]
}

/** `FolderTree.tsx:66-77`. */
interface FolderNodeProps {
  folder: FolderNode
  level: number
  expandedFolders: Set<number>
  selectedFolder: number | null
  toggleFolder: (id: number) => void
  handleFolderClick: (id: number | null) => void
  openCreateDialog: (id: number) => void
  openRenameDialog: (folder: { id: number; name: string }) => void
  openMoveDialog: (id: number) => void
  openDeleteDialog: (id: number) => void
}

/** One tree row plus its subtree — `FolderTree.tsx:79-191`. */
const FolderNodeItem = ({
  folder,
  level,
  expandedFolders,
  selectedFolder,
  toggleFolder,
  handleFolderClick,
  openCreateDialog,
  openRenameDialog,
  openMoveDialog,
  openDeleteDialog
}: FolderNodeProps) => {
  const isExpanded = expandedFolders.has(folder.id)
  const isSelected = selectedFolder === folder.id
  const hasChildren = folder.children.length > 0

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder-drop-${folder.id}`,
    data: { type: 'folder', id: folder.id }
  })

  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `folder-drag-${folder.id}`,
    data: { type: 'folder', id: folder.id, parentId: folder.parent_id }
  })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div key={folder.id} style={style} ref={setDragRef} {...attributes} {...listeners}>
      <ContextMenu>
        <ContextMenuTrigger>
          <div
            ref={setDropRef}
            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${isSelected ? 'bg-accent' : ''
              } ${isOver ? 'bg-primary/20 ring-2 ring-primary/50' : 'hover:bg-accent'}`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
            onClick={() => handleFolderClick(folder.id)}
          >
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  toggleFolder(folder.id)
                }}
                className="p-0 hover:bg-accent-foreground/10 rounded"
                onPointerDown={(e) => e.stopPropagation()} // Prevent drag start on toggle
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-4" />}
            {isExpanded ? (
              <FolderOpen className="h-4 w-4 text-primary" />
            ) : (
              <Folder className="h-4 w-4 text-primary" />
            )}
            {/* DF-22 rank 17: no `truncate`, no `title` — a long name widens the row inside the
                sidebar's ScrollArea. `MoveDialog.tsx:172` truncates; this does not. */}
            <span className="text-sm flex-1">{folder.name}</span>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={() => openCreateDialog(folder.id)}>
            <FolderPlus className="h-4 w-4 mr-2" />
            New Subfolder
          </ContextMenuItem>
          <ContextMenuItem onClick={() => openRenameDialog(folder)}>
            <Edit2 className="h-4 w-4 mr-2" />
            Rename
          </ContextMenuItem>
          <ContextMenuItem onClick={() => openMoveDialog(folder.id)}>
            <Move className="h-4 w-4 mr-2" />
            Move to...
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={() => openDeleteDialog(folder.id)}
            className="text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {isExpanded && folder.children.length > 0 && (
        <div>
          {folder.children.map((child) => (
            <FolderNodeItem
              key={child.id}
              folder={child}
              level={level + 1}
              expandedFolders={expandedFolders}
              selectedFolder={selectedFolder}
              toggleFolder={toggleFolder}
              handleFolderClick={handleFolderClick}
              openCreateDialog={openCreateDialog}
              openRenameDialog={openRenameDialog}
              openMoveDialog={openMoveDialog}
              openDeleteDialog={openDeleteDialog}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const FolderTree = forwardRef<FolderTreeHandle, FolderTreeProps>(({ onFolderSelect, hideCreateButton = false }, ref) => {
  const { data: folders } = useFolders()
  // Everything starts collapsed — `:195`.
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set())
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [parentFolderId, setParentFolderId] = useState<number | null>(null)
  const [folderToRename, setFolderToRename] = useState<{ id: number; name: string } | null>(null)
  const [folderToDelete, setFolderToDelete] = useState<number | null>(null)
  const [folderToMove, setFolderToMove] = useState<{ id: number; parent_id: number | null } | null>(null)

  const { mutate: createFolder } = useCreateFolder()
  const { mutate: updateFolder } = useUpdateFolder()
  const { mutate: deleteFolder } = useDeleteFolder()

  const openCreateDialog = (parentId: number | null = null) => {
    setParentFolderId(parentId)
    setNewFolderName('')
    setCreateDialogOpen(true)
  }

  const openDeleteDialog = (folderId: number) => {
    setFolderToDelete(folderId)
    setDeleteDialogOpen(true)
  }

  const openMoveDialog = (folderId: number) => {
    const folder = folders?.find((f) => f.id === folderId)
    if (folder) {
      setFolderToMove({ id: folder.id, parent_id: folder.parent_id })
      setMoveDialogOpen(true)
    }
  }

  // No dependency array — `:231-235`, as shipped.
  useImperativeHandle(ref, () => ({
    openCreateDialog,
    openDeleteDialog,
    openMoveDialog
  }))

  /**
   * `:237-262`.
   *
   * ⚠️ **DF-22 rank 8 IS THIS FUNCTION.** `if (parent)` has no `else`, so a node whose `parent_id`
   * is missing from the map never reaches `rootFolders` **or** a parent's `children` — it simply
   * ceases to exist in the UI. Two ways that happens:
   *   (i) a parent cycle created by dragging a folder onto its own descendant
   *       (`EnhancedSavedDocuments.tsx` `handleDragEnd`) — in `A → B → A` neither has
   *       `parent_id === null`, so **both vanish**, permanently, with no error;
   *   (ii) partial data — invisible folders, no warning.
   * Rescuing orphans to root, or detecting the cycle, is the fix (spec §15 D-6). Do not.
   */
  const buildFolderTree = (folders: FolderRow[]): FolderNode[] => {
    const folderMap = new Map<number, FolderNode>()
    const rootFolders: FolderNode[] = []

    folders?.forEach((folder) => {
      folderMap.set(folder.id, {
        id: folder.id,
        name: folder.name,
        parent_id: folder.parent_id,
        children: [],
      })
    })

    folderMap.forEach((folder) => {
      if (folder.parent_id === null) {
        rootFolders.push(folder)
      } else {
        const parent = folderMap.get(folder.parent_id)
        if (parent) {
          parent.children.push(folder)
        }
      }
    })

    return rootFolders
  }

  // `:264` — rebuilt on every render, deliberately not memoised.
  const folderTree = buildFolderTree(folders || [])

  const toggleFolder = (folderId: number) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }

  const handleFolderClick = (folderId: number | null) => {
    setSelectedFolder(folderId)
    if (onFolderSelect) {
      onFolderSelect(folderId)
    }
  }

  /**
   * `:283-302`.
   *
   * DF-22 rank 17 — `.trim()` gates the check only; the **untrimmed** `newFolderName` is what gets
   * sent (`:291`). `parent_id: parentFolderId || undefined` also means a `parentFolderId` of `0`
   * would become `undefined`; ids are 1-based so it never bites, and it is the source's expression.
   */
  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name')
      return
    }

    createFolder(
      {
        name: newFolderName,
        parent_id: parentFolderId || undefined,
      },
      {
        onSuccess: () => {
          setCreateDialogOpen(false)
          setNewFolderName('')
          setParentFolderId(null)
        },
      }
    )
  }

  /** `:304-323`. Untrimmed name again — DF-22 rank 17. */
  const handleRenameFolder = () => {
    if (!folderToRename || !newFolderName.trim()) {
      toast.error('Please enter a folder name')
      return
    }

    updateFolder(
      {
        id: folderToRename.id,
        updates: { name: newFolderName },
      },
      {
        onSuccess: () => {
          setRenameDialogOpen(false)
          setNewFolderName('')
          setFolderToRename(null)
        },
      }
    )
  }

  /** `:325-337`. */
  const handleConfirmDelete = () => {
    if (folderToDelete) {
      deleteFolder(folderToDelete, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setFolderToDelete(null)
          if (selectedFolder === folderToDelete) {
            handleFolderClick(null)
          }
        }
      })
    }
  }

  const openRenameDialog = (folder: { id: number; name: string }) => {
    setFolderToRename(folder)
    setNewFolderName(folder.name)
    setRenameDialogOpen(true)
  }

  // `:345-348` — declared here, after the handlers, exactly as in the source.
  const { setNodeRef: setRootDropRef, isOver: isRootOver } = useDroppable({
    id: 'folder-drop-root',
    data: { type: 'folder', id: null } // Root folder
  })

  return (
    <div className="space-y-2">
      {/* Root folder — droppable, selectable, and deliberately WITHOUT a context menu, so a
          root-level folder cannot be created by right-click (spec §10.1). */}
      <div
        ref={setRootDropRef}
        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${selectedFolder === null ? 'bg-accent' : ''
          } ${isRootOver ? 'bg-primary/20 ring-2 ring-primary/50' : 'hover:bg-accent'}`}
        onClick={() => handleFolderClick(null)}
      >
        <Home className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Root</span>
      </div>

      {/* Folder tree */}
      <div className="space-y-1">
        {folderTree.map((folder) => (
          <FolderNodeItem
            key={folder.id}
            folder={folder}
            level={0}
            expandedFolders={expandedFolders}
            selectedFolder={selectedFolder}
            toggleFolder={toggleFolder}
            handleFolderClick={handleFolderClick}
            openCreateDialog={openCreateDialog}
            openRenameDialog={openRenameDialog}
            openMoveDialog={openMoveDialog}
            openDeleteDialog={openDeleteDialog}
          />
        ))}
      </div>

      {/* Create folder button — DEAD IN THE SHIPPED APP: both call sites pass
          `hideCreateButton={true}` (`EnhancedSavedDocuments.tsx:188`, `:298`) and render their own
          pinned button instead (spec §10.1, §13). Ported because the prop that disables it is part
          of the contract. */}
      {!hideCreateButton && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => openCreateDialog(null)}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Folder
        </Button>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              {parentFolderId
                ? 'Create a subfolder in the selected folder'
                : 'Create a new folder in the root directory'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder()
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
            <DialogDescription>
              Enter a new name for "{folderToRename?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameFolder()
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameFolder}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog — the wording is accurate and matches both the schema
          (`folders.parent_id ON DELETE CASCADE`, `documents/preambles.folder_id ON DELETE SET NULL`)
          and the Worker's unconditional delete. Note the docs' "delete only if empty" claim is false
          (spec §14 C-2). */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the folder
              and all its subfolders.
              <br /><br />
              <strong>Note:</strong> Documents and preambles inside this folder will NOT be deleted.
              They will be moved to the Root folder.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Folder Dialog */}
      {folderToMove && (
        <MoveDialog
          open={moveDialogOpen}
          onOpenChange={setMoveDialogOpen}
          sourceId={folderToMove.id}
          sourceType="folder"
          currentParentId={folderToMove.parent_id}
          onMoveSuccess={() => {
            // If the moved folder was selected, we might want to refresh or something,
            // but react-query should handle the data update.
          }}
        />
      )}
    </div>
  )
})

FolderTree.displayName = 'FolderTree'
